import React, { createContext, useContext, useState, useCallback } from 'react';
import axiosInstance from '../../utils/axiosConfig';

export interface DemographicsResponse {
  patientId?: string | number;
  departmentId?: string | number;
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  suffix?: string | null;
  dateOfBirth?: string;
  sex?: string;
  maritalStatus?: string;
  occupation?: string | number | null;
  address?: {
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
    countryCode?: string | null;
  };
  driverLicense?: {
    hasDriverLicense?: boolean;
    driverLicenseNumber?: string | null;
    driverLicenseState?: string | null;
    driverLicenseExpirationDate?: string | null;
  };
  contactInfo?: {
    email?: string | null;
    mobilePhone?: string | null;
    homePhone?: string | null;
    workPhone?: string | null;
  };
  billingInfo?: {
    guarantorFirstName?: string | null;
    guarantorLastName?: string | null;
    guarantorMiddleName?: string | null;
    guarantorSuffix?: string | null;
    guarantorDateOfBirth?: string | null;
    guarantorRelationshipToPatient?: string | null | number;
    guarantorAddress1?: string | null;
    guarantorAddress2?: string | null;
    guarantorCity?: string | null;
    guarantorState?: string | null;
    guarantorZip?: string | null;
    guarantorCountryCode?: string | null;
    guarantorCountryCode3166?: string | null;
    guarantorEmail?: string | null;
    guarantorPhone?: string | null;
    guarantorAddressSameAsPatient?: boolean;
  };
  emergencyContact?: {
    name?: string | null;
    homePhone?: string | null;
    mobilePhone?: string | null;
    relationship?: string | null;
    contactPreference?: string | null;
  };
  language?: string;
  ethnicity?: unknown;
  race?: unknown;
  assignedSexAtBirth?: string;
  genderIdentity?: unknown;
  preferredPronouns?: string;
  sexualOrientation?: string;
  firstNameUsed?: string;
  communicationPreference?: {
    announcementEmail?: boolean;
    announcementPhone?: boolean;
    announcementSms?: boolean;
    appointmentEmail?: boolean;
    appointmentPhone?: boolean;
    appointmentSms?: boolean;
    billingEmail?: boolean;
    billingPhone?: boolean;
    billingSms?: boolean;
    labEmail?: boolean;
    labPhone?: boolean;
    labSms?: boolean;
  };
  primaryProvider?: {
    providerId?: number;
    displayName?: string;
    schedulingName?: string;
    firstName?: string;
    lastName?: string;
    specialty?: string;
    npi?: string;
  };
  insurances?: {
    insuranceId?: string | number;
    insurancePackageId?: string | number;
    insurancePackageName?: string | null;
    memberId?: string | null;
  }[];
}

export interface DemographicsUpdatePayload {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffix?: string;
  dateOfBirth?: string;
  sex?: string;
  email?: string;
  primaryProviderId?: number;
  address?: {
    address1?: string | undefined;
    address2?: string | undefined;
    city?: string | undefined;
    state?: string | undefined;
    zip?: string | undefined;
    countryCode?: string | undefined;
  };
  contactInfo?: {
    email?: string | undefined;
    mobilePhone?: string | undefined;
    homePhone?: string | undefined;
    workPhone?: string | undefined;
  };
  race?: string;
  ethnicity?: string;
  language?: string;
  maritalStatus?: string;
  occupation?: string | number | undefined;
  genderIdentity?: string | number | undefined;
  sexualOrientation?: string | undefined;
  preferredPronouns?: string | undefined;
  assignedSexAtBirth?: string | undefined;
  preferredName?: string;
  firstNameUsed?: string;
  emergencyContact?: {
    name?: string | undefined;
    mobilePhone?: string | undefined;
    homePhone?: string | undefined;
    relationship?: string | undefined;
    contactPreference?: string | undefined;
  };
  communicationPreference?: {
    announcementEmail?: boolean | undefined;
    announcementPhone?: boolean | undefined;
    announcementSms?: boolean | undefined;
    appointmentEmail?: boolean | undefined;
    appointmentPhone?: boolean | undefined;
    appointmentSms?: boolean | undefined;
    billingEmail?: boolean | undefined;
    billingPhone?: boolean | undefined;
    billingSms?: boolean | undefined;
    labEmail?: boolean | undefined;
    labPhone?: boolean | undefined;
    labSms?: boolean | undefined;
  };
  driverLicense?: {
    driverLicenseNumber?: string | undefined;
    driverLicenseState?: string | undefined;
    driverLicenseExpirationDate?: string | undefined;
  };
  billingInfo?: {
    guarantorFirstName?: string | undefined;
    guarantorLastName?: string | undefined;
    guarantorMiddleName?: string | undefined;
    guarantorSuffix?: string | undefined;
    guarantorDateOfBirth?: string | undefined;
    guarantorRelationshipToPatient?: string | undefined;
    guarantorAddress1?: string | undefined;
    guarantorAddress2?: string | undefined;
    guarantorCity?: string | undefined;
    guarantorState?: string | undefined;
    guarantorZip?: string | undefined;
    guarantorCountryCode?: string | undefined;
    guarantorEmail?: string | undefined;
    guarantorPhone?: string | undefined;
    guarantorAddressSameAsPatient?: boolean;
  };
}

interface DemographicsContextType {
  demographics: DemographicsResponse | null;
  loading: boolean;
  error: string | null;
  refreshDemographics: () => void;
  updateDemographics: (payload: DemographicsUpdatePayload) => Promise<void>;
}

const DemographicsContext = createContext<DemographicsContextType | undefined>(undefined);

interface DemographicsProviderProps {
  appointmentId: string | undefined;
  children: React.ReactNode;
}

const DemographicsProvider: React.FC<DemographicsProviderProps> = ({
  appointmentId,
  children,
}) => {
  const [demographics, setDemographics] = useState<DemographicsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDemographics = useCallback(async () => {
    if (!appointmentId) {
      setError("Missing appointment id");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/demographics`, {
        headers: { client: 'axis', domain: 'axis' }
      });
      
      setDemographics(response.data);
      setError(null);
    } catch (err) {
      setError("Unable to load your information. Please try again.");
      console.error("Failed to fetch demographics", err);
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  React.useEffect(() => {
    if (appointmentId) {
      fetchDemographics();
    }
  }, [appointmentId, fetchDemographics]);

  const refreshDemographics = useCallback(() => {
    if (!appointmentId) {
      setError("Missing appointment id");
      return;
    }

    fetchDemographics();
  }, [appointmentId, fetchDemographics]);

  const updateDemographicsHandler = useCallback(async (payload: DemographicsUpdatePayload) => {
    if (!appointmentId) {
      setError("Missing appointment id");
      throw new Error("Missing appointment id");
    }

    setLoading(true);
    setError(null);

    try {
      await axiosInstance.put(`/api/athena/digital-checkin/appointment/${encodeURIComponent(appointmentId)}/demographics`, payload, {
        headers: { client: 'axis', domain: 'axis' }
      });
      
      refreshDemographics();
    } catch (err) {
      setError("Unable to save your information. Please try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [appointmentId, refreshDemographics]);

  const value: DemographicsContextType = {
    demographics,
    loading,
    error,
    refreshDemographics,
    updateDemographics: updateDemographicsHandler,
  };

  return (
    <DemographicsContext.Provider value={value}>
      {children}
    </DemographicsContext.Provider>
  );
};

const useDemographics = () => {
  const context = useContext(DemographicsContext);
  if (context === undefined) {
    throw new Error("useDemographics must be used within a DemographicsProvider");
  }
  return context;
};

export { DemographicsProvider, useDemographics };
